import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SuperAdminAuthProvider, useSuperAdminAuth } from './context/SuperAdminAuthContext';
import Layout from './components/layout/Layout';
import SuperAdminLayout from './components/layout/SuperAdminLayout';
import Login from './pages/Login';
import Setup from './pages/Setup';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import AddCustomer from './pages/AddCustomer';
import CustomerDetail from './pages/CustomerDetail';
import Bills from './pages/Bills';
import CreateBill from './pages/CreateBill';
import BillDetail from './pages/BillDetail';
import Inventory from './pages/Inventory';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import SuperAdminLogin from './pages/superadmin/SuperAdminLogin';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import Shops from './pages/superadmin/Shops';
import ShopDetail from './pages/superadmin/ShopDetail';
import ConfigOptions from './pages/superadmin/ConfigOptions';
import SuperAdminInventory from './pages/superadmin/SuperAdminInventory';
import SuperAdminFabric from './pages/superadmin/SuperAdminFabric';
import SuperAdminAppVersion from './pages/superadmin/SuperAdminAppVersion';
import SuperAdminSales from './pages/superadmin/SuperAdminSales';
import SuperAdminPayments from './pages/superadmin/SuperAdminPayments';
import SuperAdminSaleDetail from './pages/superadmin/SuperAdminSaleDetail';
import SuperAdminCustomers from './pages/superadmin/SuperAdminCustomers';
import SuperAdminCustomerDetail from './pages/superadmin/SuperAdminCustomerDetail';
import SuperAdminActivity from './pages/superadmin/SuperAdminActivity';
import SuperAdminStaff from './pages/superadmin/SuperAdminStaff';
import SuperAdminReports from './pages/superadmin/SuperAdminReports';
import './App.css';

const PrivateRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" />;
  return children;
};

const SuperAdminPrivateRoute = ({ children }) => {
  const { superAdmin, loading } = useSuperAdminAuth();
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!superAdmin) return <Navigate to="/superadmin/login" />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const { superAdmin } = useSuperAdminAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/add" element={<AddCustomer />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="customers/:id/edit" element={<AddCustomer editMode />} />
        <Route path="bills" element={<Bills />} />
        <Route path="bills/create" element={<CreateBill />} />
        <Route path="bills/:id" element={<BillDetail />} />
        <Route path="inventory" element={<PrivateRoute adminOnly><Inventory /></PrivateRoute>} />
        <Route path="reports" element={<PrivateRoute adminOnly><Reports /></PrivateRoute>} />
        <Route path="settings" element={<PrivateRoute adminOnly><Settings /></PrivateRoute>} />
      </Route>

      <Route path="/superadmin/login" element={superAdmin ? <Navigate to="/superadmin" /> : <SuperAdminLogin />} />
      <Route path="/superadmin" element={<SuperAdminPrivateRoute><SuperAdminLayout /></SuperAdminPrivateRoute>}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="shops" element={<Shops />} />
        <Route path="shops/:id" element={<ShopDetail />} />
        <Route path="config" element={<ConfigOptions />} />
        <Route path="inventory" element={<SuperAdminInventory />} />
        <Route path="fabric" element={<SuperAdminFabric />} />
        <Route path="app-version" element={<SuperAdminAppVersion />} />
        <Route path="sales" element={<SuperAdminSales />} />
        <Route path="sales/:id" element={<SuperAdminSaleDetail />} />
        <Route path="payments" element={<SuperAdminPayments />} />
        <Route path="customers" element={<SuperAdminCustomers />} />
        <Route path="customers/:id" element={<SuperAdminCustomerDetail />} />
        <Route path="activity" element={<SuperAdminActivity />} />
        <Route path="staff" element={<SuperAdminStaff />} />
        <Route path="reports" element={<SuperAdminReports />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SuperAdminAuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{ duration: 3000, style: { fontSize: '13.5px' } }}
          />
        </BrowserRouter>
      </SuperAdminAuthProvider>
    </AuthProvider>
  );
}